import { useSyncExternalStore } from 'react';
import type { TaskDto } from '@werft/contracts';

/**
 * Briefing „Arbeitsfreude“ AF02: Erledigen bewegt nicht überraschend die nächste Checkbox unter den Mauszeiger. Eine
 * eben abgehakte Aufgabe bleibt durchgestrichen an ihrem Platz, bis der Zeiger die Liste verlässt (oder nach einigen
 * Sekunden) – dann rücken die übrigen nach. Rein lokal, nur für die eigene Aktion; fremde Änderungen wirken sofort.
 */
const HOLD_MS = 5000;
let entries = new Map<string, TaskDto>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();
const emit = () => {
  entries = new Map(entries);
  for (const l of listeners) l();
};

/**
 * Etappe D (Szenario „Schnellliste“): Wer eine eben abgehakte, noch stehende Aufgabe wieder anklickt, braucht den
 * Serverstand nach dem Erledigen (Version, Zeitpunkt) – der Schnappschuss in der Zeile ist älter und führte zu einem
 * stillen Versionskonflikt. Darum merken wir uns das Ergebnis des Erledigens.
 */
const confirmed = new Map<string, Promise<TaskDto | undefined>>();

/** Erledigen merken; `latestDone()` liefert danach den bestätigten Stand */
export function trackDone(id: string, done: Promise<unknown>) {
  confirmed.set(
    id,
    done.then(
      (r) => (r && typeof r === 'object' && 'version' in r ? (r as TaskDto) : undefined),
      () => undefined,
    ),
  );
}

/** Bestätigter Stand nach dem eigenen Erledigen (wartet, falls es noch läuft) */
export async function latestDone(task: TaskDto): Promise<TaskDto> {
  const r = await confirmed.get(task.id);
  confirmed.delete(task.id);
  return r && r.version >= task.version ? r : task;
}

export function linger(task: TaskDto) {
  entries.set(task.id, { ...task, completedAt: task.completedAt ?? new Date().toISOString() });
  clearTimeout(timers.get(task.id));
  timers.set(task.id, setTimeout(() => release([task.id]), HOLD_MS));
  emit();
}

/** Freigeben (Zeiger verlässt die Liste, wieder geöffnet, Zeit abgelaufen) */
export function release(ids?: string[]) {
  const which = ids ?? [...entries.keys()];
  let changed = false;
  for (const id of which) {
    if (entries.delete(id)) changed = true;
    clearTimeout(timers.get(id));
    timers.delete(id);
  }
  if (changed) emit();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const NONE: TaskDto[] = [];
let cache = { src: entries, byList: new Map<string, TaskDto[]>() };

/** Noch sichtbare, eben erledigte Aufgaben einer Liste */
export function useLingering(listId: string): TaskDto[] {
  const all = useSyncExternalStore(subscribe, () => entries);
  if (cache.src !== all) cache = { src: all, byList: new Map() };
  let mine = cache.byList.get(listId);
  if (!mine) {
    mine = [...all.values()].filter((x) => x.listId === listId);
    cache.byList.set(listId, mine.length ? mine : NONE);
  }
  return mine.length ? mine : NONE;
}

/** Offene Aufgaben plus eben erledigte an ihrer alten Stelle (Positionen sind byteweise sortierbar) */
export function withLingering(tasks: TaskDto[], lingering: TaskDto[]): TaskDto[] {
  if (!lingering.length) return tasks;
  const known = new Set(tasks.map((x) => x.id));
  const extra = lingering.filter((x) => !known.has(x.id));
  if (!extra.length) return tasks;
  return [...tasks, ...extra].sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
}
